import { useEffect, useState, useCallback } from 'react';
import { apiService } from '@/services/apiService';
import { AgendaItem, AgendaType } from '@/types';

/**
 * Shape retornado por GET /api/agenda (backend/src/index.ts:2866)
 * — mapeado diretamente da implementação, não adivinhado.
 */
interface RawAgendaItem {
  id: string;
  type:
    | 'TENDER_ACTION'
    | 'TENDER_DEADLINE'
    | 'CONTRACT_END'
    | 'ORDER_DELIVERY'
    | 'DOCUMENT_EXPIRATION'
    | 'COMM_DEADLINE'
    | 'PORTAL_RENEWAL'
    | 'TENDER_TASK';
  title: string;
  date: string;              // "YYYY-MM-DD"
  time: string | null;       // "HH:MM" ou null
  status: string;
  relatedId?: string;
  route?: string;
  daysRemaining?: number;
  severity?: 'normal' | 'attention' | 'high' | 'critical';
  meta: {
    organ?: string;
    portal?: string;
    state?: string;
    value?: number;
    companyId?: number | string;
    issueDate?: string;
    number?: string;
    type?: string;
    date?: string;
    tenderId?: string;
    tenderTitle?: string;
    assigneeName?: string | null;
    priority?: string;
  };
}

// Mapeia o type do backend para os 4 grupos da agenda editorial
const TYPE_MAP: Record<RawAgendaItem['type'], AgendaType> = {
  TENDER_ACTION:       'edital',
  TENDER_DEADLINE:     'edital',
  CONTRACT_END:        'contrato',
  ORDER_DELIVERY:      'contrato',
  DOCUMENT_EXPIRATION: 'certidao',
  PORTAL_RENEWAL:      'certidao',
  COMM_DEADLINE:       'sessao',
  TENDER_TASK:         'tarefa',
};

// Label humano por tipo raw (usado no subtitle quando não há organ)
const RAW_LABEL: Record<RawAgendaItem['type'], string> = {
  TENDER_ACTION:       'Próxima ação',
  TENDER_DEADLINE:     'Prazo do edital',
  CONTRACT_END:        'Vencimento de contrato',
  ORDER_DELIVERY:      'Entrega de empenho',
  DOCUMENT_EXPIRATION: 'Vencimento de documento',
  PORTAL_RENEWAL:      'Renovação de portal',
  COMM_DEADLINE:       'Prazo de comunicação',
  TENDER_TASK:         'Tarefa da licitação',
};

export function adaptAgendaItem(raw: RawAgendaItem): AgendaItem {
  // Combina date (YYYY-MM-DD) com time (HH:MM) para obter ISO completo
  const timeStr = raw.time && /^\d{1,2}:\d{2}/.test(raw.time) ? raw.time : '09:00';
  const iso = new Date(`${raw.date}T${timeStr.length === 4 ? '0' + timeStr : timeStr}:00`);
  const hourNum = iso.getHours();

  // Priority: TENDER_DEADLINE sem ação e certidões próximas → high; demais → medium
  const priority: AgendaItem['priority'] = raw.severity === 'critical' || raw.severity === 'high' || raw.meta?.priority === 'HIGH' || raw.meta?.priority === 'CRITICAL'
    ? 'high'
    : raw.type === 'TENDER_DEADLINE' || raw.type === 'DOCUMENT_EXPIRATION'
      ? 'high'
      : 'medium';

  return {
    id: raw.id,
    date: iso.toISOString(),
    hour: hourNum,
    type: TYPE_MAP[raw.type],
    title: raw.title,
    subtitle: raw.type === 'TENDER_TASK'
      ? [raw.meta?.assigneeName ? `Responsável: ${raw.meta.assigneeName}` : null, raw.meta?.tenderTitle || raw.meta?.organ].filter(Boolean).join(' · ')
      : raw.meta?.organ || raw.meta?.portal || RAW_LABEL[raw.type],
    value: raw.meta?.value,
    relatedId: raw.relatedId || raw.id.replace(/^[a-z]+-[a-z]+-/, ''),
    priority,
    daysRemaining: raw.daysRemaining,
    severity: raw.severity,
    route: raw.route,
    done: false,
  };
}

export function useAgendaEvents() {
  const [events, setEvents] = useState<AgendaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    apiService
      .getAgenda()
      .then(raw => setEvents(((raw as RawAgendaItem[]) || []).map(adaptAgendaItem)))
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let alive = true;
    apiService
      .getAgenda()
      .then(raw => {
        if (!alive) return;
        setEvents(((raw as RawAgendaItem[]) || []).map(adaptAgendaItem));
        setLoading(false);
      })
      .catch(e => {
        if (!alive) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });
    return () => { alive = false; };
  }, []);

  return { events, loading, error, reload: load };
}
